import React, { useState, useEffect, useCallback } from 'react';
import { sets, images } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SetManager from '../components/SetManager';

const Sets = () => {
  const navigate = useNavigate();
  const [userSets, setUserSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showManager, setShowManager] = useState(false);

  const handleManagerUpdate = useCallback(() => {
    sets.getAll().then(fetchedSets => {
      setUserSets(fetchedSets);
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedSets = await sets.getAll();
        setUserSets(fetchedSets);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowManager(false);
      }
    };

    if (showManager) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showManager]);


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-900">Sets</h1>
            <p className="text-sm text-gray-500">
              Create and manage sets of individual coins or dynamic sets based on tags and filters
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowManager(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Manage Sets
            </button>
          </div>
        </div>

        {/* Set Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {userSets.map(set => (
            <motion.div
              key={set.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border border-gray-200 shadow hover:shadow-lg transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(`/sets/${set.id}`)}
            >
                <div className="aspect-square flex flex-col">
                  <div className="flex-1 bg-white rounded-t-lg overflow-hidden relative">
                    {set.cover_image?.file_path ? (
                      <>
                        <img
                          src={images.getImage(set.cover_image.file_path, set.cover_image.source)}
                          alt={`First coin in ${set.name}`}
                          className="w-full h-full object-contain"
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-b-lg">
                    <h3 className="text-lg font-medium text-gray-900">{set.name}</h3>
                    {set.description && (
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{set.description}</p>
                    )}
                    <p className="mt-2 text-sm text-gray-500 flex gap-2">
                      <span>{set.coin_count || 0} {Number(set.coin_count || 0) === 1 ? 'coin' : 'coins'}</span>
                      <span>•</span>
                      <span>{set.field_preferences?.visible_fields?.length || 0} fields</span>
                      {set.type === 'dynamic' && (
                        <>
                          <span>•</span>
                          <span>{((set.filter_criteria?.all_tags?.length || 0) + (set.filter_criteria?.any_tags?.length || 0))} tags</span>
                          {(set.filter_criteria?.column_filters || set.filter_criteria?.filters) && (
                            <>
                              <span>•</span>
                              <span>
                                {(
                                  (set.filter_criteria?.column_filters ? Object.values(set.filter_criteria.column_filters).filter(v => v).length : 0) +
                                  (set.filter_criteria?.filters?.length || 0)
                                )} filters
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
            {userSets.length === 0 && (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No sets created yet. Click "Manage Sets" to create your first set.</p>
              </div>
            )}
          </div>

        {/* Set Manager Modal */}
        <AnimatePresence>
          {showManager && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Manage Sets</h2>
                  <button
                    onClick={() => setShowManager(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4">
                  <SetManager onUpdate={handleManagerUpdate} navigate={navigate} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default Sets;
